import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  AreaChart,
} from "@tremor/react";
import { auth } from "../../firebase";
import FeatureGate from "../FeatureGate/FeatureGate";
import "./Analytics.css";

// Sample analytics data for preview (shown to non-premium users)
const SAMPLE_ANALYTICS_DATA = {
  overview: {
    visitors: 128,
    visits: 256,
    pageviews: 512,
    bounceRate: 45,
    visitDuration: 150, // 2m 30s
    viewsPerVisit: 2.0
  },
  sources: [
    { source: 'Google', visitors: 45 },
    { source: 'Direct', visitors: 38 },
    { source: 'Twitter', visitors: 22 },
    { source: 'LinkedIn', visitors: 15 },
    { source: 'Dribbble', visitors: 8 }
  ],
  countries: [
    { country: 'US', visitors: 52 },
    { country: 'GB', visitors: 24 },
    { country: 'DE', visitors: 18 },
    { country: 'CA', visitors: 12 },
    { country: 'AU', visitors: 10 }
  ],
  devices: [
    { device: 'Desktop', visitors: 83 },
    { device: 'Mobile', visitors: 38 },
    { device: 'Tablet', visitors: 7 }
  ],
  browsers: [
    { browser: 'Chrome', visitors: 65 },
    { browser: 'Safari', visitors: 38 },
    { browser: 'Firefox', visitors: 15 },
    { browser: 'Edge', visitors: 10 }
  ],
  operatingSystems: [
    { os: 'Windows', visitors: 52 },
    { os: 'macOS', visitors: 45 },
    { os: 'iOS', visitors: 18 },
    { os: 'Android', visitors: 13 }
  ],
  timeseries: [
    { date: 'Jan 24', Visitors: 15 },
    { date: 'Jan 25', Visitors: 22 },
    { date: 'Jan 26', Visitors: 18 },
    { date: 'Jan 27', Visitors: 25 },
    { date: 'Jan 28', Visitors: 20 },
    { date: 'Jan 29', Visitors: 28 },
    { date: 'Jan 30', Visitors: 0 }
  ]
};

// Country code to flag emoji and name mapping
const countryData = {
  'US': { flag: '🇺🇸', name: 'United States' },
  'GB': { flag: '🇬🇧', name: 'United Kingdom' },
  'DE': { flag: '🇩🇪', name: 'Germany' },
  'FR': { flag: '🇫🇷', name: 'France' },
  'CA': { flag: '🇨🇦', name: 'Canada' },
  'AU': { flag: '🇦🇺', name: 'Australia' },
  'JP': { flag: '🇯🇵', name: 'Japan' },
  'IN': { flag: '🇮🇳', name: 'India' },
  'BR': { flag: '🇧🇷', name: 'Brazil' },
  'IT': { flag: '🇮🇹', name: 'Italy' },
  'ES': { flag: '🇪🇸', name: 'Spain' },
  'NL': { flag: '🇳🇱', name: 'Netherlands' },
  'SE': { flag: '🇸🇪', name: 'Sweden' },
  'NO': { flag: '🇳🇴', name: 'Norway' },
  'DK': { flag: '🇩🇰', name: 'Denmark' },
  'FI': { flag: '🇫🇮', name: 'Finland' },
  'PL': { flag: '🇵🇱', name: 'Poland' },
  'CH': { flag: '🇨🇭', name: 'Switzerland' },
  'AT': { flag: '🇦🇹', name: 'Austria' },
  'BE': { flag: '🇧🇪', name: 'Belgium' },
  'MX': { flag: '🇲🇽', name: 'Mexico' },
  'AR': { flag: '🇦🇷', name: 'Argentina' },
  'KR': { flag: '🇰🇷', name: 'South Korea' },
  'CN': { flag: '🇨🇳', name: 'China' },
  'TW': { flag: '🇹🇼', name: 'Taiwan' },
  'HK': { flag: '🇭🇰', name: 'Hong Kong' },
  'SG': { flag: '🇸🇬', name: 'Singapore' },
  'NZ': { flag: '🇳🇿', name: 'New Zealand' },
  'IE': { flag: '🇮🇪', name: 'Ireland' },
  'PT': { flag: '🇵🇹', name: 'Portugal' },
  'RU': { flag: '🇷🇺', name: 'Russia' },
  'ZA': { flag: '🇿🇦', name: 'South Africa' },
  'IL': { flag: '🇮🇱', name: 'Israel' },
  'AE': { flag: '🇦🇪', name: 'UAE' },
  'SA': { flag: '🇸🇦', name: 'Saudi Arabia' },
  'TH': { flag: '🇹🇭', name: 'Thailand' },
  'ID': { flag: '🇮🇩', name: 'Indonesia' },
  'PH': { flag: '🇵🇭', name: 'Philippines' },
  'MY': { flag: '🇲🇾', name: 'Malaysia' },
  'VN': { flag: '🇻🇳', name: 'Vietnam' },
  'CL': { flag: '🇨🇱', name: 'Chile' },
  'CO': { flag: '🇨🇴', name: 'Colombia' },
  'PE': { flag: '🇵🇪', name: 'Peru' },
  'UA': { flag: '🇺🇦', name: 'Ukraine' },
  'CZ': { flag: '🇨🇿', name: 'Czech Republic' },
  'RO': { flag: '🇷🇴', name: 'Romania' },
  'HU': { flag: '🇭🇺', name: 'Hungary' },
  'GR': { flag: '🇬🇷', name: 'Greece' },
  'TR': { flag: '🇹🇷', name: 'Turkey' },
  'PK': { flag: '🇵🇰', name: 'Pakistan' },
  'BD': { flag: '🇧🇩', name: 'Bangladesh' },
  'EG': { flag: '🇪🇬', name: 'Egypt' },
  'NG': { flag: '🇳🇬', name: 'Nigeria' },
  'KE': { flag: '🇰🇪', name: 'Kenya' },
  'MA': { flag: '🇲🇦', name: 'Morocco' },
};

// Source icons - using emoji/text for common sources
const sourceIcons = {
  'Direct / None': '🔗',
  'direct': '🔗',
  'Google': 'G',
  'google': 'G',
  'google.com': 'G',
  'Facebook': 'f',
  'facebook': 'f',
  'facebook.com': 'f',
  'Twitter': '𝕏',
  'twitter': '𝕏',
  'twitter.com': '𝕏',
  't.co': '𝕏',
  'x.com': '𝕏',
  'LinkedIn': 'in',
  'linkedin': 'in',
  'linkedin.com': 'in',
  'Instagram': '📷',
  'instagram': '📷',
  'instagram.com': '📷',
  'YouTube': '▶',
  'youtube': '▶',
  'youtube.com': '▶',
  'Reddit': '🔶',
  'reddit': '🔶',
  'reddit.com': '🔶',
  'Pinterest': '📌',
  'pinterest': '📌',
  'pinterest.com': '📌',
  'TikTok': '♪',
  'tiktok': '♪',
  'tiktok.com': '♪',
  'Bing': 'b',
  'bing': 'b',
  'bing.com': 'b',
  'DuckDuckGo': '🦆',
  'duckduckgo': '🦆',
  'duckduckgo.com': '🦆',
  'GitHub': '🐙',
  'github': '🐙',
  'github.com': '🐙',
  'Dribbble': '🏀',
  'dribbble': '🏀',
  'dribbble.com': '🏀',
  'Behance': 'Bē',
  'behance': 'Bē',
  'behance.net': 'Bē',
  'figmafolio.com': 'F',
  'join.com': 'J',
};

// Device icons
const deviceIcons = {
  'Desktop': '💻',
  'Mobile': '📱',
  'Tablet': '📱',
};

// Browser icons
const browserIcons = {
  'Chrome': '🌐',
  'Safari': '🧭',
  'Firefox': '🦊',
  'Edge': '🌊',
  'Opera': '🔴',
  'Samsung Browser': '📱',
  'IE': '🔷',
  'Brave': '🦁',
  'Vivaldi': '🎨',
  'Arc': '⭕',
};

// OS icons
const osIcons = {
  'Windows': '🪟',
  'Mac': '🍎',
  'macOS': '🍎',
  'iOS': '📱',
  'Android': '🤖',
  'Linux': '🐧',
  'Chrome OS': '💻',
  'Ubuntu': '🐧',
};

// Format duration as "Xm Ys"
function formatDuration(seconds) {
  if (!seconds || seconds === 0) return "0s";
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins === 0) return `${secs}s`;
  if (secs === 0) return `${mins}m`;
  return `${mins}m ${secs}s`;
}

// Format number with commas
function formatNumber(num) {
  return num?.toLocaleString() || '0';
}

// Format time for hourly data
function formatTimeLabel(dateStr, period) {
  if (period === 'day' && dateStr) {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true }).toLowerCase();
  }
  if (dateStr) {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return dateStr;
}

// Get source icon
function getSourceIcon(source) {
  const normalized = source?.toLowerCase() || '';

  // Check exact match first
  if (sourceIcons[source]) return sourceIcons[source];
  if (sourceIcons[normalized]) return sourceIcons[normalized];

  // Check if source contains known platforms
  for (const [key, icon] of Object.entries(sourceIcons)) {
    if (normalized.includes(key.toLowerCase())) return icon;
  }

  // Default icon
  return '🔗';
}

// Custom Bar Item component - Clean list style (no colored bars)
function BarItem({ icon, label, value, maxValue, showCountryCode, countryCode }) {
  return (
    <div className="analytics-bar-item">
      <div className="analytics-bar-content">
        <div className="analytics-bar-label">
          {showCountryCode && countryCode && (
            <span className="analytics-country-code">{countryCode}</span>
          )}
          {icon && (
            <span className="analytics-bar-icon analytics-bar-icon-emoji">{icon}</span>
          )}
          <span className="analytics-bar-text">{label}</span>
        </div>
        <span className="analytics-bar-value">{formatNumber(value)}</span>
      </div>
    </div>
  );
}

// Metric Card Component - Plausible style with inline change indicator (clickable)
function MetricCard({ title, value, change, format = "number", isHighlighted, onClick, metricKey }) {
  let formattedValue = value;
  if (format === "duration") {
    formattedValue = formatDuration(value);
  } else if (format === "percent") {
    formattedValue = `${Math.round(value || 0)}%`;
  } else if (format === "decimal") {
    formattedValue = (value || 0).toFixed(2);
  } else {
    formattedValue = formatNumber(value);
  }

  const isPositive = change > 0;
  const isNegative = change < 0;
  const changeColor = isPositive ? "analytics-change-positive" : isNegative ? "analytics-change-negative" : "analytics-change-neutral";
  const changeArrow = isPositive ? "↑" : isNegative ? "↓" : "";

  return (
    <div
      className={`analytics-metric-cell ${onClick ? 'clickable' : ''} ${isHighlighted ? 'selected' : ''}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      <p className={`analytics-metric-title ${isHighlighted ? 'highlighted' : ''}`}>{title}</p>
      <div className="analytics-metric-value-row">
        <span className="analytics-metric-value">{formattedValue}</span>
        {change !== undefined && change !== 0 && (
          <span className={`analytics-metric-change-inline ${changeColor}`}>
            {changeArrow} {Math.abs(change)}%
          </span>
        )}
      </div>
    </div>
  );
}

// Loading Skeleton - Plausible style
function LoadingSkeleton() {
  return (
    <div className="analytics-loading">
      <div className="analytics-skeleton-metrics">
        <div className="analytics-skeleton-card" />
        <div className="analytics-skeleton-card" />
        <div className="analytics-skeleton-card" />
        <div className="analytics-skeleton-card" />
        <div className="analytics-skeleton-card" />
        <div className="analytics-skeleton-card" />
      </div>
      <div className="analytics-skeleton-chart" />
      <div className="analytics-skeleton-row">
        <div className="analytics-skeleton-list" />
        <div className="analytics-skeleton-list" />
      </div>
      <div className="analytics-skeleton-row">
        <div className="analytics-skeleton-list" />
        <div className="analytics-skeleton-list" />
      </div>
    </div>
  );
}

// Error State
function ErrorState({ onRetry }) {
  return (
    <div className="analytics-error">
      <div className="analytics-error-icon">⚠️</div>
      <h3 className="analytics-error-title">Failed to load analytics</h3>
      <p className="analytics-error-text">
        There was an error loading your analytics data.
      </p>
      <button className="analytics-retry-btn" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

// List Subheader
function ListSubheader({ leftLabel, rightLabel }) {
  return (
    <div className="analytics-list-subheader">
      <span>{leftLabel}</span>
      <span>{rightLabel}</span>
    </div>
  );
}

// Generate empty chart data based on period
function generateEmptyChartData(period) {
  const now = new Date();
  const data = [];

  if (period === 'day') {
    // Hourly intervals for today
    for (let hour = 0; hour < 24; hour += 3) {
      const date = new Date(now);
      date.setHours(hour, 0, 0, 0);
      const label = date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true }).toLowerCase();
      data.push({ date: label, Visitors: 0 });
    }
  } else if (period === '7d') {
    // Daily intervals for last 7 days
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      data.push({ date: label, Visitors: 0 });
    }
  } else if (period === '30d') {
    // Daily intervals for last 30 days
    for (let i = 29; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      data.push({ date: label, Visitors: 0 });
    }
  } else if (period === 'month') {
    // Days in current month
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(now.getFullYear(), now.getMonth(), i);
      const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      data.push({ date: label, Visitors: 0 });
    }
  } else if (period === '12mo') {
    // Monthly intervals for last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now);
      date.setMonth(date.getMonth() - i);
      const label = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      data.push({ date: label, Visitors: 0 });
    }
  }

  return data;
}

// Metric configuration for clickable metrics
const METRIC_CONFIG = {
  visitors: { key: 'visitors', label: 'UNIQUE VISITORS', format: 'number', dataKey: 'Visitors' },
  visits: { key: 'visits', label: 'TOTAL VISITS', format: 'number', dataKey: 'Visits' },
  pageviews: { key: 'pageviews', label: 'TOTAL PAGEVIEWS', format: 'number', dataKey: 'Pageviews' },
  viewsPerVisit: { key: 'viewsPerVisit', label: 'VIEWS PER VISIT', format: 'decimal', dataKey: 'Views per Visit' },
  bounceRate: { key: 'bounceRate', label: 'BOUNCE RATE', format: 'percent', dataKey: 'Bounce Rate' },
  visitDuration: { key: 'visitDuration', label: 'VISIT DURATION', format: 'duration', dataKey: 'Visit Duration' },
};

export default function Analytics({
  projectId,
  userId,
  generatedUrl,
  customDomain,
  subscriptionType = "regular",
  setShowPaymentModal,
}) {
  // Check if user has analytics access (Solo and Pro only)
  const hasAnalyticsAccess = subscriptionType === 'solo' || subscriptionType === 'pro';

  const [period, setPeriod] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [sources, setSources] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [devices, setDevices] = useState([]);
  const [browsers, setBrowsers] = useState([]);
  const [operatingSystems, setOperatingSystems] = useState([]);
  const [locationsTab, setLocationsTab] = useState(0);
  const [devicesTab, setDevicesTab] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState('visitors');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAnalytics = useCallback(async () => {
    if (!projectId || !userId) return;

    setLoading(true);
    setError(null);

    try {
      const baseUrl = `/api/plausible`;
      const params = `projectId=${projectId}&period=${period}`;

      // The endpoint derives the owner from a verified Firebase ID token
      // (not from a query param), following the api/domains.js pattern.
      if (!auth.currentUser) {
        setLoading(false);
        return;
      }
      const idToken = await auth.currentUser.getIdToken();
      const requestInit = { headers: { Authorization: `Bearer ${idToken}` } };

      // Fetch all metrics in parallel (removed pages, added browsers and os)
      const [
        overviewRes,
        timeseriesRes,
        sourcesRes,
        countriesRes,
        citiesRes,
        devicesRes,
        browsersRes,
        osRes,
      ] = await Promise.all([
        fetch(`${baseUrl}?${params}&metric=overview`, requestInit),
        fetch(`${baseUrl}?${params}&metric=timeseries`, requestInit),
        fetch(`${baseUrl}?${params}&metric=sources`, requestInit),
        fetch(`${baseUrl}?${params}&metric=countries`, requestInit),
        fetch(`${baseUrl}?${params}&metric=cities`, requestInit),
        fetch(`${baseUrl}?${params}&metric=devices`, requestInit),
        fetch(`${baseUrl}?${params}&metric=browsers`, requestInit),
        fetch(`${baseUrl}?${params}&metric=os`, requestInit),
      ]);

      // Check for errors
      if (!overviewRes.ok) {
        throw new Error("Failed to fetch analytics");
      }

      // Log non-200 breakdown responses so failures are distinguishable from genuinely zero data
      const breakdownChecks = [
        ['timeseries', timeseriesRes],
        ['sources', sourcesRes],
        ['countries', countriesRes],
        ['cities', citiesRes],
        ['devices', devicesRes],
        ['browsers', browsersRes],
        ['os', osRes],
      ];
      await Promise.all(breakdownChecks.map(async ([name, res]) => {
        if (!res.ok) {
          let body = '';
          try { body = await res.clone().text(); } catch (_) {}
          console.error(`[Analytics] ${name} breakdown failed: HTTP ${res.status}`, body);
        }
      }));

      // Parse responses
      const [
        overviewData,
        timeseriesData,
        sourcesData,
        countriesData,
        citiesData,
        devicesData,
        browsersData,
        osData,
      ] = await Promise.all([
        overviewRes.json(),
        timeseriesRes.json(),
        sourcesRes.json(),
        countriesRes.json(),
        citiesRes.json(),
        devicesRes.json(),
        browsersRes.json(),
        osRes.json(),
      ]);

      setOverview(overviewData.data);
      setTimeseries(timeseriesData.data || []);
      setSources(sourcesData.data || []);
      setCountries(countriesData.data || []);
      setCities(citiesData.data || []);
      setDevices(devicesData.data || []);
      setBrowsers(browsersData.data || []);
      setOperatingSystems(osData.data || []);
    } catch (err) {
      console.error("Analytics fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, userId, period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleRetry = () => {
    fetchAnalytics();
  };

  // Period options - Dropdown style
  const periodOptions = [
    { value: "day", label: "Today" },
    { value: "7d", label: "Last 7 Days" },
    { value: "30d", label: "Last 30 Days" },
    { value: "month", label: "This Month" },
    { value: "12mo", label: "Last 12 Months" },
  ];

  const currentPeriodLabel = periodOptions.find(p => p.value === period)?.label || "Last 7 Days";

  // Get max values for bar proportions
  const maxSourceVisitors = Math.max(...sources.map(s => s.visitors || 0), 1);
  const maxCountryVisitors = Math.max(...countries.map(c => c.visitors || 0), 1);
  const maxCityVisitors = Math.max(...cities.map(c => c.visitors || 0), 1);
  const maxDeviceVisitors = Math.max(...devices.map(d => d.visitors || 0), 1);
  const maxBrowserVisitors = Math.max(...browsers.map(b => b.visitors || 0), 1);
  const maxOsVisitors = Math.max(...operatingSystems.map(o => o.visitors || 0), 1);

  // Format timeseries data for chart - use actual data or generate empty data
  // Include all metrics for the selected metric display
  const chartData = timeseries.length > 0
    ? timeseries.map((item) => {
        const viewsPerVisit = item.visits > 0 ? item.pageviews / item.visits : 0;
        return {
          date: formatTimeLabel(item.date, period),
          Visitors: item.visitors || 0,
          Visits: item.visits || 0,
          Pageviews: item.pageviews || 0,
          'Views per Visit': Math.round(viewsPerVisit * 100) / 100,
          'Bounce Rate': item.bounce_rate || 0,
          'Visit Duration': item.visit_duration || 0,
        };
      })
    : generateEmptyChartData(period);

  // Get the data key for the currently selected metric
  const selectedMetricConfig = METRIC_CONFIG[selectedMetric];
  const chartCategory = selectedMetricConfig?.dataKey || 'Visitors';
  const chartTitle = selectedMetricConfig?.label?.replace(/^(TOTAL |UNIQUE )/, '') || 'Visitors';

  // Custom value formatter based on selected metric
  const formatChartValue = (value) => {
    if (selectedMetric === 'visitDuration') {
      return formatDuration(value);
    } else if (selectedMetric === 'bounceRate') {
      return `${Math.round(value)}%`;
    } else if (selectedMetric === 'viewsPerVisit') {
      return value.toFixed(2);
    }
    return formatNumber(Math.round(value));
  };

  // Calculate Y-axis max and ticks for integer display
  const getYAxisConfig = () => {
    const values = chartData.map(d => d[chartCategory] || 0);
    const maxValue = Math.max(...values, 1);

    // For metrics that should always be integers (visitors, visits, pageviews)
    if (['visitors', 'visits', 'pageviews'].includes(selectedMetric)) {
      // Determine nice step size
      let step = 1;
      if (maxValue > 100) step = Math.ceil(maxValue / 5 / 10) * 10;
      else if (maxValue > 50) step = 10;
      else if (maxValue > 20) step = 5;
      else if (maxValue > 10) step = 2;

      const niceMax = Math.ceil(maxValue / step) * step;
      return { maxValue: niceMax, allowDecimals: false };
    }

    return { maxValue: undefined, allowDecimals: true };
  };

  const yAxisConfig = getYAxisConfig();

  // Period dropdown select handler
  const handlePeriodSelect = (value) => {
    setPeriod(value);
    setDropdownOpen(false);
  };

  // Show locked state for Free/Basic users using FeatureGate
  if (!hasAnalyticsAccess) {
    const sampleOverview = SAMPLE_ANALYTICS_DATA.overview;
    const sampleTimeseries = SAMPLE_ANALYTICS_DATA.timeseries;

    // Analytics preview UI to show in blurred/greyed state
    const analyticsPreviewUI = (
      <>
        <div className="analytics-header">
          <h1 className="form-title">Analytics</h1>
          <div className="analytics-period-dropdown">
            <button className="analytics-dropdown-btn" disabled>
              Last 7 Days
              <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Metric Cards */}
        <div className="analytics-metrics-grid">
          <div className="analytics-metrics-row-grid">
            <MetricCard
              title="UNIQUE VISITORS"
              value={sampleOverview.visitors}
              isHighlighted={true}
            />
            <div className="analytics-metric-divider-v" />
            <MetricCard
              title="TOTAL VISITS"
              value={sampleOverview.visits}
            />
          </div>
          <div className="analytics-metric-divider-h" />
          <div className="analytics-metrics-row-grid">
            <MetricCard
              title="TOTAL PAGEVIEWS"
              value={sampleOverview.pageviews}
            />
            <div className="analytics-metric-divider-v" />
            <MetricCard
              title="VIEWS PER VISIT"
              value={sampleOverview.viewsPerVisit}
              format="decimal"
            />
          </div>
          <div className="analytics-metric-divider-h" />
          <div className="analytics-metrics-row-grid">
            <MetricCard
              title="BOUNCE RATE"
              value={sampleOverview.bounceRate}
              format="percent"
            />
            <div className="analytics-metric-divider-v" />
            <MetricCard
              title="VISIT DURATION"
              value={sampleOverview.visitDuration}
              format="duration"
            />
          </div>
        </div>

        {/* Time Series Chart */}
        <Card className="analytics-chart-card">
          <div className="analytics-chart-header">
            <span className="analytics-chart-title">Visitors</span>
          </div>
          <AreaChart
            className="analytics-area-chart"
            data={sampleTimeseries}
            index="date"
            categories={["Visitors"]}
            colors={["indigo"]}
            valueFormatter={(v) => formatNumber(Math.round(v))}
            showLegend={false}
            showGridLines={true}
            curveType="monotone"
            yAxisWidth={48}
            minValue={0}
          />
        </Card>
      </>
    );

    return (
      <div className="analytics-container">
        <FeatureGate
          requiredTier="solo"
          currentTier={subscriptionType}
          title="See who's viewing your portfolio"
          message="Unlock detailed analytics to track visitors, referrers, and engagement on your portfolio."
          onUpgradeClick={() => setShowPaymentModal && setShowPaymentModal()}
          blur={true}
        >
          {analyticsPreviewUI}
        </FeatureGate>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="analytics-header">
          <h1 className="form-title">Analytics</h1>
        </div>
        <LoadingSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-container">
        <div className="analytics-header">
          <h1 className="form-title">Analytics</h1>
        </div>
        <ErrorState onRetry={handleRetry} />
      </div>
    );
  }

  // Always show dashboard, even with no data (show zeros)
  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h1 className="form-title">Analytics</h1>
        <div className="analytics-period-dropdown" ref={dropdownRef}>
          <button
            className="analytics-dropdown-btn"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            {currentPeriodLabel}
            <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {dropdownOpen && (
            <div className="analytics-dropdown-menu">
              {periodOptions.map((option) => (
                <button
                  key={option.value}
                  className={`analytics-dropdown-item ${period === option.value ? 'active' : ''}`}
                  onClick={() => handlePeriodSelect(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Metric Cards - 2x3 Grid with dividers (clickable to change graph) */}
      <div className="analytics-metrics-grid">
        <div className="analytics-metrics-row-grid">
          <MetricCard
            title="UNIQUE VISITORS"
            value={overview?.visitors || 0}
            change={overview?.visitorsChange}
            isHighlighted={selectedMetric === 'visitors'}
            onClick={() => setSelectedMetric('visitors')}
            metricKey="visitors"
          />
          <div className="analytics-metric-divider-v" />
          <MetricCard
            title="TOTAL VISITS"
            value={overview?.visits || 0}
            change={overview?.visitsChange}
            isHighlighted={selectedMetric === 'visits'}
            onClick={() => setSelectedMetric('visits')}
            metricKey="visits"
          />
        </div>
        <div className="analytics-metric-divider-h" />
        <div className="analytics-metrics-row-grid">
          <MetricCard
            title="TOTAL PAGEVIEWS"
            value={overview?.pageviews || 0}
            change={overview?.pageviewsChange}
            isHighlighted={selectedMetric === 'pageviews'}
            onClick={() => setSelectedMetric('pageviews')}
            metricKey="pageviews"
          />
          <div className="analytics-metric-divider-v" />
          <MetricCard
            title="VIEWS PER VISIT"
            value={overview?.viewsPerVisit || 0}
            change={overview?.viewsPerVisitChange}
            format="decimal"
            isHighlighted={selectedMetric === 'viewsPerVisit'}
            onClick={() => setSelectedMetric('viewsPerVisit')}
            metricKey="viewsPerVisit"
          />
        </div>
        <div className="analytics-metric-divider-h" />
        <div className="analytics-metrics-row-grid">
          <MetricCard
            title="BOUNCE RATE"
            value={overview?.bounceRate || 0}
            change={overview?.bounceRateChange}
            format="percent"
            isHighlighted={selectedMetric === 'bounceRate'}
            onClick={() => setSelectedMetric('bounceRate')}
            metricKey="bounceRate"
          />
          <div className="analytics-metric-divider-v" />
          <MetricCard
            title="VISIT DURATION"
            value={overview?.visitDuration || 0}
            change={overview?.visitDurationChange}
            format="duration"
            isHighlighted={selectedMetric === 'visitDuration'}
            onClick={() => setSelectedMetric('visitDuration')}
            metricKey="visitDuration"
          />
        </div>
      </div>

      {/* Time Series Chart */}
      <Card className="analytics-chart-card">
        <div className="analytics-chart-header">
          <span className="analytics-chart-title">{chartTitle}</span>
        </div>
        <AreaChart
          className="analytics-area-chart"
          data={chartData}
          index="date"
          categories={[chartCategory]}
          colors={["indigo"]}
          valueFormatter={formatChartValue}
          showLegend={false}
          showGridLines={true}
          curveType="monotone"
          yAxisWidth={48}
          minValue={0}
          maxValue={yAxisConfig.maxValue}
          allowDecimals={yAxisConfig.allowDecimals}
        />
      </Card>

      {/* 2x2 Bottom Grid - Sources, Locations, Devices */}
      <div className="analytics-grid-2x2">
        {/* Sources */}
        <Card className="analytics-list-card">
          <div className="analytics-card-header">
            <span className="analytics-card-tab active">Sources</span>
          </div>
          <ListSubheader leftLabel="Source" rightLabel="Visitors" />
          <div className="analytics-list-content">
            {sources.length > 0 ? (
              sources.map((item, index) => (
                <BarItem
                  key={index}
                  icon={getSourceIcon(item.source || 'Direct / None')}
                  label={item.source || 'Direct / None'}
                  value={item.visitors}
                  maxValue={maxSourceVisitors}
                />
              ))
            ) : (
              <p className="analytics-no-data">No data yet</p>
            )}
          </div>
        </Card>

        {/* Locations - Countries/Cities with working tabs */}
        <Card className="analytics-list-card">
          <div className="analytics-card-header">
            <button
              className={`analytics-card-tab ${locationsTab === 0 ? 'active' : ''}`}
              onClick={() => setLocationsTab(0)}
            >
              Countries
            </button>
            <button
              className={`analytics-card-tab ${locationsTab === 1 ? 'active' : ''}`}
              onClick={() => setLocationsTab(1)}
            >
              Cities
            </button>
          </div>
          {locationsTab === 0 ? (
            <>
              <ListSubheader leftLabel="Country" rightLabel="Visitors" />
              <div className="analytics-list-content">
                {countries.length > 0 ? (
                  countries.map((item, index) => {
                    const country = countryData[item.country] || { flag: '🌍', name: item.country };
                    return (
                      <BarItem
                        key={index}
                        icon={country.flag}
                        label={country.name || item.country || 'Unknown'}
                        value={item.visitors}
                        maxValue={maxCountryVisitors}
                      />
                    );
                  })
                ) : (
                  <p className="analytics-no-data">No data yet</p>
                )}
              </div>
            </>
          ) : (
            <>
              <ListSubheader leftLabel="City" rightLabel="Visitors" />
              <div className="analytics-list-content">
                {cities.length > 0 ? (
                  cities.map((item, index) => (
                    <BarItem
                      key={index}
                      icon={null}
                      label={item.city || 'Unknown'}
                      value={item.visitors}
                      maxValue={maxCityVisitors}
                      showCountryCode={true}
                      countryCode={item.country}
                    />
                  ))
                ) : (
                  <p className="analytics-no-data">No data yet</p>
                )}
              </div>
            </>
          )}
        </Card>

        {/* Devices - Browsers/OS/Devices with working tabs */}
        <Card className="analytics-list-card">
          <div className="analytics-card-header">
            <button
              className={`analytics-card-tab ${devicesTab === 0 ? 'active' : ''}`}
              onClick={() => setDevicesTab(0)}
            >
              Browsers
            </button>
            <button
              className={`analytics-card-tab ${devicesTab === 1 ? 'active' : ''}`}
              onClick={() => setDevicesTab(1)}
            >
              OS
            </button>
            <button
              className={`analytics-card-tab ${devicesTab === 2 ? 'active' : ''}`}
              onClick={() => setDevicesTab(2)}
            >
              Devices
            </button>
          </div>
          {devicesTab === 0 ? (
            <>
              <ListSubheader leftLabel="Browser" rightLabel="Visitors" />
              <div className="analytics-list-content">
                {browsers.length > 0 ? (
                  browsers.map((item, index) => (
                    <BarItem
                      key={index}
                      icon={browserIcons[item.browser] || '🌐'}
                      label={item.browser || 'Unknown'}
                      value={item.visitors}
                      maxValue={maxBrowserVisitors}
                    />
                  ))
                ) : (
                  <p className="analytics-no-data">No data yet</p>
                )}
              </div>
            </>
          ) : devicesTab === 1 ? (
            <>
              <ListSubheader leftLabel="Operating System" rightLabel="Visitors" />
              <div className="analytics-list-content">
                {operatingSystems.length > 0 ? (
                  operatingSystems.map((item, index) => (
                    <BarItem
                      key={index}
                      icon={osIcons[item.os] || '💻'}
                      label={item.os || 'Unknown'}
                      value={item.visitors}
                      maxValue={maxOsVisitors}
                    />
                  ))
                ) : (
                  <p className="analytics-no-data">No data yet</p>
                )}
              </div>
            </>
          ) : (
            <>
              <ListSubheader leftLabel="Device" rightLabel="Visitors" />
              <div className="analytics-list-content">
                {devices.length > 0 ? (
                  devices.map((item, index) => (
                    <BarItem
                      key={index}
                      icon={deviceIcons[item.device] || '📱'}
                      label={item.device || 'Unknown'}
                      value={item.visitors}
                      maxValue={maxDeviceVisitors}
                    />
                  ))
                ) : (
                  <p className="analytics-no-data">No data yet</p>
                )}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
